import { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { debounce } from "lodash";

import { ApiRequest } from "API";
import { SCROLLABLE_TABLE_LOADING } from "Components/Tables/AgGrid/ListDataScroll/constants";
import toastify from "Components/Toastify";
import { PREVIOUS_PATHS } from "Constants/common";
import i18n from "I18n";
import { closeConfirmationModal, setConfirmationModal } from "Redux/global/globalSlice";
import { setLoading } from "Redux/settings/settingsSlice";
import { AppDispatch, RootState } from "Redux/types";
import { takePreviousPaths } from "Utils/Functions";

const CODE_ERROR_NOT_ALLOWED_DATA = "error-not-allowed-data";

export const useAppDispatch: () => AppDispatch = useDispatch;

export function useAppSelector<TSelected>(selector: (state: RootState) => TSelected): TSelected {
  return useSelector(selector, shallowEqual);
}

/**
 * Qaytarib bo'lmaydigan amaldan (rad etish, qaytarib olish va h.k.) oldin tasdiq oynasini ko'rsatadi.
 * "Ha" bosilganda amal bajariladi, "Yo'q" bosilganda hech narsa o'zgarmaydi.
 */
export const useConfirmBeforeAction = () => {
  const dispatch = useAppDispatch();
  return (onConfirm: () => void, options?: { confirmText?: string; subTitle?: string; title?: string }) =>
    dispatch(
      setConfirmationModal({
        confirmText: options?.confirmText,
        modalType: "warning",
        onCancel: () => dispatch(closeConfirmationModal()),
        onConfirm: () => {
          dispatch(closeConfirmationModal());
          onConfirm();
        },
        subTitle: options?.subTitle,
        title: options?.title,
        visible: true,
      })
    );
};

export const useForceUpdate = () => {
  const [_, setRenderComponent] = useState(false);
  return () => setRenderComponent((renderComponent) => !renderComponent);
};
export const useDebounce = (callback: any, delay: 500) => useCallback(debounce(callback, 500), []);

export function useInterval(callback: any, delay: any) {
  const savedCallback: any = useRef();
  //Remember the last callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  //Set up then Interval
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => {
        clearInterval(id);
      };
    }
  }, [callback, delay]);
}

export const useDidUpdateOnce = (callback: Function, dependencies: any[]) => {
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const isWorked = useRef(false);
  return useEffect(() => {
    if (isWorked.current) return;
    if (isFirstLoading) {
      setIsFirstLoading(false);
      return;
    }
    isWorked.current = true;
    callback();
  }, dependencies);
};

export const useEffectDidUpdate = (callback: Function, dependencies: any[]) => {
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  return useEffect(() => {
    if (isFirstLoading) {
      setIsFirstLoading(false);
      return;
    }
    callback();
  }, dependencies);
};

export const useUnload = (showModal: boolean) => {
  const active = useCallback((e) => {
    e.preventDefault();
    e.returnValue = "";
  }, []);

  useEffect(() => {
    if (showModal) {
      window.onbeforeunload = active;
    } else {
      window.onbeforeunload = null;
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [showModal]);
};

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" && !!window.matchMedia ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQueryList = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mediaQueryList.matches);

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", onChange);
      return () => mediaQueryList.removeEventListener("change", onChange);
    }
    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }, [query]);

  return matches;
};

export const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref: any = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useBrowserPreviosPathsKeeper = () => {
  const history = useHistory();

  useEffect(() => {
    history.listen((location, action) => {
      const paths = takePreviousPaths();
      switch (action) {
        case "POP":
          localStorage.setItem(PREVIOUS_PATHS, JSON.stringify(paths.slice(0, paths.length - 1)));
          break;
        case "PUSH":
          if (paths.length > 9) {
            paths.shift();
          }
          localStorage.setItem(PREVIOUS_PATHS, JSON.stringify([...paths, window.location.href]));
          break;
        case "REPLACE":
          localStorage.setItem(PREVIOUS_PATHS, JSON.stringify([...paths.slice(0, paths.length - 1), window.location.href]));
          break;
      }
    });
  }, [history]);
};

const showNoAccessToDocumentModal = (dispatch: AppDispatch) =>
  dispatch(
    setConfirmationModal({
      confirmText: i18n.t("clear"),
      hideSubTitle: true,
      modalType: "warning",
      onConfirm: () => dispatch(closeConfirmationModal()),
      title: i18n.t("youHaveNotAccessToThisDocument"),
      visible: true,
    })
  );

/**
 * Hujjatni ochish endpointi rad etsa, hujjat sahifasi bo'sh qolmaydi: huquq yo'q bo'lsa shu haqda oyna,
 * boshqa xatoda odatiy xabar ko'rsatiladi va fallbackPath berilgan bo'lsa ro'yxatga qaytiladi.
 * So'rov { isToast: false } bilan yuboriladi — xabarni shu funksiya chiqaradi.
 */
export const useDocumentOpenError = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  return useCallback(
    (code?: string, fallbackPath?: string) => {
      if (code === CODE_ERROR_NOT_ALLOWED_DATA) {
        showNoAccessToDocumentModal(dispatch);
      } else {
        toastify.result(code);
      }
      fallbackPath && history.replace(fallbackPath);
    },
    [dispatch, history]
  );
};

export const useAccessForDocument = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const checkAccess = ({
    accessed = true,
    apiEndpoint,
    callback,
    params,
    redirectPath,
  }: {
    accessed?: boolean;
    apiEndpoint: string;
    callback?: () => void;
    params?: object;
    redirectPath?: string;
  }) => {
    if (accessed) {
      dispatch(setLoading({ key: apiEndpoint, status: true }));
      dispatch(setLoading({ key: SCROLLABLE_TABLE_LOADING, status: true }));
      return ApiRequest.Get(apiEndpoint, params)
        .then((res) => {
          if (res.data.success) {
            if (res.data?.data) {
              redirectPath && history.push(redirectPath);
              callback && callback();
            } else {
              showNoAccessToDocumentModal(dispatch);
            }
          }
        })
        .finally(() => {
          dispatch(setLoading({ key: SCROLLABLE_TABLE_LOADING, status: false }));
          dispatch(setLoading({ key: apiEndpoint, status: false }));
        });
    } else {
      redirectPath && history.push(redirectPath);
      callback && callback();
    }
  };
  return { checkAccess };
};
